import React, { useState, useEffect } from 'react';
import {
  Calculator,
  Plus,
  Search,
  CheckCircle2,
  Trash2,
  Building2,
  ArrowRight,
  TrendingUp,
  FileText,
  MessageSquare,
} from 'lucide-react';
import { BOQItem, BOQItemLine } from '../types/index.ts';
import { WhatsAppShareModal, WhatsAppShareData } from '../components/common/WhatsAppShareModal.tsx';

interface BOQPageProps {
  onNavigate: (route: string, param?: string) => void;
}

export const BOQPage: React.FC<BOQPageProps> = ({ onNavigate }) => {
  const [boqs, setBoqs] = useState<BOQItem[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [selectedBoq, setSelectedBoq] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);

  // WhatsApp Sharing State
  const [whatsAppModalOpen, setWhatsAppModalOpen] = useState(false);
  const [shareData, setShareData] = useState<WhatsAppShareData | null>(null);

  // New BOQ Form state
  const [newProjectId, setNewProjectId] = useState('');
  const [newTitle, setNewTitle] = useState('Detailed Civil & Finishing Works BOQ');
  const [revisionNumber, setRevisionNumber] = useState(0);
  const [lines, setLines] = useState<BOQItemLine[]>([
    {
      itemNumber: 1,
      description: 'Supplying and laying 600x600 porcelain floor tiles with adhesive & grout',
      unit: 'Sft',
      quantity: 1500,
      materialRate: 280,
      labourRate: 65,
      otherCost: 20,
      marginPercent: 15,
    },
    {
      itemNumber: 2,
      description: '2x2 ft Gypsum tile false ceiling with powder coated aluminium grid frame',
      unit: 'Sft',
      quantity: 1200,
      materialRate: 110,
      labourRate: 40,
      otherCost: 15,
      marginPercent: 15,
    },
    {
      itemNumber: 3,
      description: 'Berger Matt Finish plastic emulsion painting on walls after primer & putty',
      unit: 'Sft',
      quantity: 2800,
      materialRate: 22,
      labourRate: 15,
      otherCost: 3,
      marginPercent: 15,
    },
  ]);

  const fetchBoqs = async () => {
    try {
      setLoading(true);
      const [bRes, pRes] = await Promise.all([
        fetch('/api/boqs'),
        fetch('/api/projects'),
      ]);
      if (bRes.ok) {
        const bData = await bRes.json();
        if (Array.isArray(bData)) {
          setBoqs(bData);
          if (bData.length > 0) fetchBoqDetail(bData[0].id);
        }
      }
      if (pRes.ok) {
        const pData = await pRes.json();
        if (Array.isArray(pData)) {
          setProjects(pData);
          if (pData.length > 0) setNewProjectId(String(pData[0].id));
        }
      }
    } catch (err) {
      console.warn('BOQ fetch notice:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchBoqDetail = async (id: number) => {
    try {
      const res = await fetch(`/api/boqs/${id}`);
      if (res.ok) {
        const data = await res.json();
        setSelectedBoq(data);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchBoqs();
  }, []);

  const handleAddLine = () => {
    setLines([
      ...lines,
      {
        itemNumber: lines.length + 1,
        description: '',
        unit: 'Sft',
        quantity: 100,
        materialRate: 0,
        labourRate: 0,
        otherCost: 0,
        marginPercent: 15,
      },
    ]);
  };

  const handleRemoveLine = (idx: number) => {
    if (lines.length > 1) {
      setLines(lines.filter((_, i) => i !== idx));
    }
  };

  const handleLineChange = (index: number, field: keyof BOQItemLine, value: any) => {
    const updated = [...lines];
    updated[index] = { ...updated[index], [field]: value };
    setLines(updated);
  };

  // Live calculations for new BOQ
  const calculateTotals = () => {
    let subtotal = 0;
    let totalMaterial = 0;
    let totalLabour = 0;
    let totalOther = 0;

    for (const item of lines) {
      const q = Number(item.quantity || 0);
      const mRate = Number(item.materialRate || 0);
      const lRate = Number(item.labourRate || 0);
      const oCost = Number(item.otherCost || 0);
      const margin = Number(item.marginPercent || 15);

      const baseUnitCost = mRate + lRate + oCost;
      const unitRate = baseUnitCost * (1 + margin / 100);
      const totalAmount = unitRate * q;

      subtotal += totalAmount;
      totalMaterial += mRate * q;
      totalLabour += lRate * q;
      totalOther += oCost * q;
    }

    const taxAmount = (subtotal * 13) / 100;
    const grandTotal = subtotal + taxAmount;
    const estimatedCost = totalMaterial + totalLabour + totalOther;
    const estimatedProfit = subtotal - estimatedCost;

    return {
      subtotal,
      taxAmount,
      grandTotal,
      estimatedCost,
      estimatedProfit,
      totalMaterial,
      totalLabour,
      totalOther,
    };
  };

  const totals = calculateTotals();

  const handleCreateBoq = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const res = await fetch('/api/boqs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          projectId: Number(newProjectId),
          title: newTitle,
          revisionNumber: Number(revisionNumber),
          items: lines,
        }),
      });
      if (res.ok) {
        setShowAddModal(false);
        fetchBoqs();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleApprove = async (id: number) => {
    try {
      const res = await fetch(`/api/boqs/${id}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ comment: 'Approved by authorized officer' }),
      });
      if (res.ok) {
        fetchBoqs();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const formatPKR = (val: string | number) => {
    return 'Rs. ' + Math.round(Number(val || 0)).toLocaleString('en-PK');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
              BOQ &amp; Estimation Engine
            </h1>
            <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-amber-100 text-amber-900 border border-amber-200">
              Auto Formula
            </span>
          </div>
          <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
            Unit Rate = (Material + Labour + Other) × (1 + Margin%). Revision history preserved.
          </p>
        </div>

        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-1.5 px-3.5 py-2 bg-[#0f2942] hover:bg-[#183d5f] text-white rounded-lg text-xs font-semibold shadow-2xs transition-colors self-start sm:self-auto"
        >
          <Plus className="w-4 h-4 text-amber-400" />
          <span>New BOQ Revision</span>
        </button>
      </div>

      {/* Main BOQ Explorer */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* BOQ Selection List */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-2xs overflow-hidden">
          <div className="p-4 border-b border-slate-100 bg-slate-50">
            <span className="text-xs font-bold text-slate-700 uppercase tracking-wider">
              Project BOQ Revisions
            </span>
          </div>

          <div className="divide-y divide-slate-100 max-h-[600px] overflow-y-auto">
            {(Array.isArray(boqs) ? boqs : []).map((b) => (
              <div
                key={b.id}
                onClick={() => fetchBoqDetail(b.id)}
                className={`p-4 cursor-pointer transition-all ${
                  selectedBoq?.id === b.id
                    ? 'bg-blue-50/70 border-l-4 border-blue-600'
                    : 'hover:bg-slate-50'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-bold text-xs text-blue-900">{b.boqNumber}</span>
                  <span
                    className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                      b.status === 'APPROVED'
                        ? 'bg-emerald-100 text-emerald-800'
                        : 'bg-amber-100 text-amber-800'
                    }`}
                  >
                    {b.status}
                  </span>
                </div>
                <div className="font-semibold text-xs text-slate-800 mt-1 line-clamp-1">{b.title}</div>
                <div className="text-[11px] text-slate-500 mt-0.5">{b.projectName}</div>
                <div className="mt-2 text-xs font-bold text-slate-900">{formatPKR(b.grandTotal)}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Selected BOQ Detail & Live Item Breakdown (Takes 2 cols) */}
        <div className="lg:col-span-2 space-y-4">
          {selectedBoq ? (
            <div className="bg-white rounded-xl border border-slate-200 shadow-2xs p-5 space-y-5">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 pb-4">
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-base font-bold text-slate-900">{selectedBoq.boqNumber}</h2>
                    <span className="text-xs font-bold px-2 py-0.5 rounded bg-slate-100 text-slate-700">
                      Revision {selectedBoq.revisionNumber}
                    </span>
                  </div>
                  <p className="text-xs text-slate-600 mt-0.5">{selectedBoq.title}</p>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShareData({
                        title: `BOQ Schedule - ${selectedBoq.boqNumber} (Rev ${selectedBoq.revisionNumber})`,
                        docType: 'BOQ',
                        referenceNumber: selectedBoq.boqNumber,
                        projectName: selectedBoq.projectName,
                        amount: formatPKR(selectedBoq.grandTotal),
                        detailsSummary: `${selectedBoq.title}. ${selectedBoq.items?.length || 0} line items. Subtotal: ${formatPKR(selectedBoq.subtotal)}, Tax: ${formatPKR(selectedBoq.taxAmount)}. Status: ${selectedBoq.status}`,
                      });
                      setWhatsAppModalOpen(true);
                    }}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-[#25D366]/20 hover:bg-[#25D366]/30 text-[#075e54] border border-[#25D366]/40 rounded-lg text-xs font-bold transition-colors"
                    title="Share BOQ via WhatsApp"
                  >
                    <MessageSquare className="w-3.5 h-3.5 text-[#25D366]" />
                    <span>WhatsApp BOQ</span>
                  </button>

                  {selectedBoq.status !== 'APPROVED' ? (
                    <button
                      onClick={() => handleApprove(selectedBoq.id)}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-semibold shadow-2xs transition-colors"
                    >
                      <CheckCircle2 className="w-4 h-4" />
                      <span>Approve BOQ</span>
                    </button>
                  ) : (
                    <div className="flex items-center gap-1 text-xs font-bold text-emerald-700 bg-emerald-50 px-3 py-1.5 rounded-lg border border-emerald-200">
                      <CheckCircle2 className="w-4 h-4" />
                      <span>Approved by {selectedBoq.approvedBy || 'Admin'}</span>
                    </div>
                  )}

                  <button
                    onClick={() => onNavigate('quotations')}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-700 hover:bg-blue-800 text-white rounded-lg text-xs font-semibold shadow-2xs transition-colors"
                  >
                    <FileText className="w-4 h-4" />
                    <span>Generate Quotation</span>
                  </button>
                </div>
              </div>

              {/* Items Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider text-[10px] border-b border-slate-200">
                    <tr>
                      <th className="py-2.5 px-3">#</th>
                      <th className="py-2.5 px-3">Scope Description</th>
                      <th className="py-2.5 px-3">Unit</th>
                      <th className="py-2.5 px-3 text-right">Qty</th>
                      <th className="py-2.5 px-3 text-right">Mat. Rate</th>
                      <th className="py-2.5 px-3 text-right">Lab. Rate</th>
                      <th className="py-2.5 px-3 text-right">Unit Rate</th>
                      <th className="py-2.5 px-3 text-right">Total (PKR)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {Array.isArray(selectedBoq.items) && selectedBoq.items.length > 0 ? (
                      selectedBoq.items.map((item: any, i: number) => (
                        <tr key={item.id || i} className="hover:bg-slate-50">
                          <td className="py-2.5 px-3 font-mono text-slate-400">{item.itemNumber}</td>
                          <td className="py-2.5 px-3 font-medium text-slate-800 max-w-xs">{item.description}</td>
                          <td className="py-2.5 px-3 text-slate-500">{item.unit}</td>
                          <td className="py-2.5 px-3 text-right font-medium">{Number(item.quantity).toLocaleString()}</td>
                          <td className="py-2.5 px-3 text-right text-slate-600">{Number(item.materialRate).toLocaleString()}</td>
                          <td className="py-2.5 px-3 text-right text-slate-600">{Number(item.labourRate).toLocaleString()}</td>
                          <td className="py-2.5 px-3 text-right font-semibold text-slate-900">{Number(item.unitRate).toLocaleString()}</td>
                          <td className="py-2.5 px-3 text-right font-bold text-blue-900">{Number(item.totalAmount).toLocaleString()}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={8} className="py-4 text-center text-slate-400">
                          No items loaded for this BOQ.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Estimation & Totals Footer */}
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5 text-xs">
                  <span className="font-bold text-slate-700 uppercase tracking-wider text-[10px]">
                    Costing Breakdown
                  </span>
                  <div className="text-slate-600">Material Cost + Labour Cost + Overhead</div>
                  <div className="text-emerald-700 font-semibold">Margin applied: 15.0% Standard</div>
                </div>

                <div className="space-y-1.5 text-xs text-right">
                  <div className="flex justify-between sm:justify-end gap-6 text-slate-600">
                    <span>Subtotal Excl. Tax:</span>
                    <span className="font-semibold text-slate-800">{formatPKR(selectedBoq.subtotal)}</span>
                  </div>
                  <div className="flex justify-between sm:justify-end gap-6 text-slate-600">
                    <span>Sindh / Punjab Sales Tax ({selectedBoq.taxPercent}%):</span>
                    <span className="font-semibold text-slate-800">{formatPKR(selectedBoq.taxAmount)}</span>
                  </div>
                  <div className="flex justify-between sm:justify-end gap-6 text-sm font-bold text-slate-900 pt-1 border-t border-slate-200">
                    <span>Grand Total Selling Price:</span>
                    <span className="text-blue-900">{formatPKR(selectedBoq.grandTotal)}</span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-xs text-slate-400">
              Select a BOQ revision to view detailed line items and pricing formulas.
            </div>
          )}
        </div>
      </div>

      {/* Add BOQ Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
          <div className="bg-white rounded-xl shadow-2xl border border-slate-200 max-w-4xl w-full p-5 space-y-4 animate-in fade-in zoom-in-95 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <h3 className="font-bold text-slate-900 text-base">Create New BOQ Revision</h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-slate-400 hover:text-slate-600 text-sm font-semibold"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleCreateBoq} className="space-y-4 text-xs">
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <label className="block font-semibold text-slate-700 mb-1">Target Project *</label>
                  <select
                    required
                    className="w-full border border-slate-200 rounded-lg p-2"
                    value={newProjectId}
                    onChange={(e) => setNewProjectId(e.target.value)}
                  >
                    {(Array.isArray(projects) ? projects : []).map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.projectNumber} - {p.projectName}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Revision Number</label>
                  <input
                    type="number"
                    className="w-full border border-slate-200 rounded-lg p-2"
                    value={revisionNumber}
                    onChange={(e) => setRevisionNumber(Number(e.target.value))}
                  />
                </div>
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">BOQ Title *</label>
                <input
                  type="text"
                  required
                  className="w-full border border-slate-200 rounded-lg p-2"
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                />
              </div>

              {/* Line items editor */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-slate-800 text-xs">Scope Line Items &amp; Rates</span>
                  <button
                    type="button"
                    onClick={handleAddLine}
                    className="text-blue-700 hover:text-blue-900 font-semibold flex items-center gap-1"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    <span>Add Item</span>
                  </button>
                </div>

                <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
                  {(Array.isArray(lines) ? lines : []).map((line, idx) => (
                    <div key={idx} className="p-3 bg-slate-50 rounded-lg border border-slate-200 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="font-bold text-slate-700">Item #{idx + 1}</span>
                        {lines.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveLine(idx)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>

                      <div className="grid grid-cols-12 gap-2">
                        <div className="col-span-6">
                          <input
                            type="text"
                            placeholder="Description"
                            required
                            className="w-full border border-slate-200 rounded p-1.5 bg-white"
                            value={line.description}
                            onChange={(e) => handleLineChange(idx, 'description', e.target.value)}
                          />
                        </div>
                        <div className="col-span-2">
                          <input
                            type="text"
                            placeholder="Unit (Sft, Rft)"
                            required
                            className="w-full border border-slate-200 rounded p-1.5 bg-white"
                            value={line.unit}
                            onChange={(e) => handleLineChange(idx, 'unit', e.target.value)}
                          />
                        </div>
                        <div className="col-span-4">
                          <input
                            type="number"
                            placeholder="Qty"
                            required
                            className="w-full border border-slate-200 rounded p-1.5 bg-white"
                            value={line.quantity}
                            onChange={(e) => handleLineChange(idx, 'quantity', Number(e.target.value))}
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-4 gap-2">
                        <div>
                          <label className="text-[10px] text-slate-500 block">Mat. Rate (Rs.)</label>
                          <input
                            type="number"
                            className="w-full border border-slate-200 rounded p-1.5 bg-white"
                            value={line.materialRate}
                            onChange={(e) => handleLineChange(idx, 'materialRate', Number(e.target.value))}
                          />
                        </div>
                        <div>
                          <label className="text-[10px] text-slate-500 block">Labour Rate (Rs.)</label>
                          <input
                            type="number"
                            className="w-full border border-slate-200 rounded p-1.5 bg-white"
                            value={line.labourRate}
                            onChange={(e) => handleLineChange(idx, 'labourRate', Number(e.target.value))}
                          />
                        </div>
                        <div>
                          <label className="text-[10px] text-slate-500 block">Other Cost (Rs.)</label>
                          <input
                            type="number"
                            className="w-full border border-slate-200 rounded p-1.5 bg-white"
                            value={line.otherCost}
                            onChange={(e) => handleLineChange(idx, 'otherCost', Number(e.target.value))}
                          />
                        </div>
                        <div>
                          <label className="text-[10px] text-slate-500 block">Margin %</label>
                          <input
                            type="number"
                            className="w-full border border-slate-200 rounded p-1.5 bg-white"
                            value={line.marginPercent}
                            onChange={(e) => handleLineChange(idx, 'marginPercent', Number(e.target.value))}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Dynamic Totals */}
              <div className="bg-blue-50/70 p-3 rounded-xl border border-blue-200 flex items-center justify-between text-xs">
                <div>
                  <div className="text-slate-600">Estimated Cost: {formatPKR(totals.estimatedCost)}</div>
                  <div className="text-emerald-700 font-semibold">Estimated Profit: {formatPKR(totals.estimatedProfit)}</div>
                </div>
                <div className="text-right">
                  <div className="text-slate-600">Tax: {formatPKR(totals.taxAmount)}</div>
                  <div className="text-sm font-bold text-blue-900">Grand Total: {formatPKR(totals.grandTotal)}</div>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50 font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-[#0f2942] hover:bg-[#183d5f] text-white rounded-lg font-semibold"
                >
                  Save BOQ Revision
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* WhatsApp Share Modal */}
      <WhatsAppShareModal
        isOpen={whatsAppModalOpen}
        onClose={() => setWhatsAppModalOpen(false)}
        data={shareData}
      />
    </div>
  );
};
